import React from 'react'
import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'

import { Text } from '@mxenabled/mxui'
import { Error as ErrorIcon } from '@mxenabled/mx-icons'

import ConnectDrawer from '../../../../common/components/ConnectDrawer'

import {
  getConnectionMessages,
  ConnectionMessageObject,
} from '../../../utils/getConnectionMessages'
import { useGlobalCopyStore } from '../../../../common'
import { NonConnectedStatuses } from '../../../../core'
import { useAccountUiStore } from '../../../hooks/useUiStore'

interface ConnectionErrorMessageProps {
  setTabValue: (val: number) => void
}

const ConnectionErrorMessage: React.FC<ConnectionErrorMessageProps> = ({ setTabValue }) => {
  const [shouldShowConnectDrawer, setShouldShowConnectDrawer] = React.useState(false)

  const { selectedMember: member } = useAccountUiStore()
  const { accounts: accountsCopy, connect: connectCopy } = useGlobalCopyStore()

  const hasError =
    member.connection_status && NonConnectedStatuses.includes(member.connection_status)

  React.useEffect(() => {
    if (hasError) {
      setTabValue(1)
    }
  }, [hasError])

  if (!hasError) return

  const statusMessages: ConnectionMessageObject = getConnectionMessages(member, connectCopy)

  // eslint-disable-next-line consistent-return
  return (
    <>
      <Paper
        sx={{
          mb: 16,
          mt: 24,
          mx: 24,
        }}
      >
        <Alert
          icon={<ErrorIcon filled={true} />}
          role="none"
          severity="error"
          sx={{
            borderLeft: 5,
            borderTopLeftRadius: 6,
            borderBottomLeftRadius: 6,
            borderColor: 'error.main',
            color: 'text.primary',
            '&.MuiPaper-root': {
              backgroundColor: 'background.paper',
            },
          }}
        >
          {statusMessages.title && (
            <AlertTitle sx={{ fontWeight: 600, marginTop: 2 }} variant="body1">
              {statusMessages.title}
            </AlertTitle>
          )}
          <Text component="p" variant="subtitle2">
            {statusMessages.main}
          </Text>
          {statusMessages.cta && (
            <Link
              onClick={() => setShouldShowConnectDrawer(true)}
              sx={{ fontSize: 15, fontWeight: 600, ml: 0 }}
            >
              {statusMessages.cta}
            </Link>
          )}
        </Alert>
      </Paper>

      {shouldShowConnectDrawer && (
        <ConnectDrawer
          loadWidgetProps={{ current_member_guid: member.guid }}
          onClose={() => setShouldShowConnectDrawer(false)}
          showConnectWidget={shouldShowConnectDrawer}
          title={accountsCopy.manage_connection}
        />
      )}
    </>
  )
}

export default observer(ConnectionErrorMessage)
